<template>
  <div class="deal-project">
    <!-- Loading -->
    <div v-if="loading" class="deal-project__loading">
      <LoadingSpinner />
    </div>

    <!-- Empty state -->
    <div v-else-if="!project" class="deal-project__empty">
      <span class="deal-project__empty-icon">
        <BaseIcon name="clipboard" size="20" />
      </span>
      <h4 class="deal-project__empty-title">{{ $t('projects.no_project_yet') }}</h4>
      <p class="deal-project__empty-description">
        {{ emptyDescription || $t('projects.no_project_yet_description') }}
      </p>
      <button type="button" class="deal-project__empty-action" @click="$emit('create-project')">
        <span>{{ createLabel || $t('projects.create_project_for_deal') }}</span>
        <BaseIcon name="plus" size="16" />
      </button>
    </div>

    <!-- Project content -->
    <div v-else class="deal-project__content">
      <!-- Cancelled context banner -->
      <div
        v-if="project.status === 'cancelled' && deal && deal.lost_at"
        class="deal-project__cancelled-banner"
      >
        <BaseIcon name="info" size="16" />
        <span>{{ $t('deals.project_cancelled_with_deal') }}</span>
      </div>

      <!-- Project card -->
      <div class="deal-project__card">
        <!-- Card header -->
        <div class="deal-project__card-header">
          <div class="deal-project__card-title-row">
            <span class="deal-project__project-number">{{ project.project_number }}</span>
            <span
              class="deal-project__status project-status"
              :class="{ ['project-status--' + project.status]: project.status }"
            >
              {{ $t(`projects.statuses.${project.status}`) }}
            </span>
          </div>
          <div v-if="displayTitle" class="deal-project__project-title">
            {{ displayTitle }}
          </div>
          <div v-if="project.expected_completion_date" class="deal-project__expected-date">
            {{ $t('projects.expected_completion') }}:
            {{ formatDateShort(project.expected_completion_date) }}
          </div>
        </div>

        <!-- Progress -->
        <div class="deal-project__progress">
          <div class="deal-project__progress-header">
            <span class="deal-project__progress-label">{{ $t('projects.progress') }}</span>
            <span class="deal-project__progress-pct">{{ progressPercent }}%</span>
          </div>
          <div class="deal-project__progress-bar-wrap">
            <div
              class="deal-project__progress-bar-fill"
              :style="{ width: progressPercent + '%' }"
            />
          </div>
          <div class="deal-project__progress-text">
            {{
              $t('projects.work_orders_progress', {
                completed: completedWorkOrders,
                total: totalWorkOrders,
              })
            }}
            ·
            {{ $t('projects.orders_progress', { completed: completedOrders, total: totalOrders }) }}
          </div>
        </div>

        <!-- Orders mini-list -->
        <div v-if="project.orders && project.orders.length > 0" class="deal-project__section">
          <h4 class="deal-project__section-title">
            {{ $t('orders.title') }} ({{ project.orders.length }})
          </h4>
          <div class="deal-project__list">
            <div v-for="order in project.orders" :key="order.id" class="deal-project__list-item">
              <span class="deal-project__item-number">{{ order.order_number }}</span>
              <StatusBadge :status="order.status" size="sm" />
            </div>
          </div>
        </div>

        <!-- Work orders mini-list -->
        <div
          v-if="project.work_orders && project.work_orders.length > 0"
          class="deal-project__section"
        >
          <h4 class="deal-project__section-title">
            {{ $t('work_orders.title') }} ({{ project.work_orders.length }})
          </h4>
          <div class="deal-project__list">
            <div
              v-for="workOrder in project.work_orders"
              :key="workOrder.id"
              class="deal-project__list-item"
            >
              <span class="deal-project__item-number">{{ workOrder.work_order_number }}</span>
              <span
                class="work-order-status"
                :class="{ ['work-order-status--' + workOrder.status]: workOrder.status }"
              >
                {{ $t(`work_orders.statuses.${workOrder.status}`) }}
              </span>
              <span v-if="getAppointmentStart(workOrder)" class="deal-project__item-date">
                <BaseIcon
                  v-if="isTentativelyPlanned(workOrder)"
                  name="edit"
                  size="12"
                  class="deal-project__tentative-icon"
                />
                {{ formatDate(getAppointmentStart(workOrder)) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Card footer -->
        <div class="deal-project__card-footer">
          <Btn
            variant="secondary"
            size="sm"
            :text="$t('projects.view_project')"
            icon="arrow-up-right"
            @click="$emit('navigate-to-project', project.id)"
          />
          <Btn
            v-if="project.status === 'completed'"
            variant="tertiary"
            size="sm"
            :text="$t('projects.reopen_project')"
            icon="refresh"
            @click="$emit('reopen-project', project.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch, onMounted } from 'vue';
  import { projectService } from '@/services/projectService';
  import { formatDate, formatDateShort } from '@/utils/date';
  import { projectDisplayTitle } from '@/utils/projectTitle';
  import { getAppointmentStart, isTentativelyPlanned } from '@/utils/workOrders';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import StatusBadge from '@/components/partials/StatusBadge.vue';

  export default defineComponent({
    name: 'DealProject',

    components: {
      BaseIcon,
      Btn,
      LoadingSpinner,
      StatusBadge,
    },

    props: {
      dealId: {
        type: String,
        required: true,
      },
      deal: {
        type: Object,
        default: null,
      },
      emptyDescription: {
        type: String,
        default: '',
      },
      createLabel: {
        type: String,
        default: '',
      },
    },

    emits: ['create-project', 'navigate-to-project', 'reopen-project'],

    setup(props, { expose }) {
      const project = ref(null);
      const loading = ref(true);

      const loadProject = async () => {
        if (!props.dealId) {
          project.value = null;
          loading.value = false;
          return;
        }
        loading.value = true;
        try {
          project.value = await projectService.getProjectByDealId(props.dealId);
        } catch (error) {
          console.error('Failed to fetch project by deal:', error);
          project.value = null;
        } finally {
          loading.value = false;
        }
      };

      const displayTitle = computed(() => projectDisplayTitle(project.value));

      expose({ refresh: loadProject });

      onMounted(loadProject);
      watch(() => props.dealId, loadProject);

      const completedWorkOrders = computed(() => {
        const orders = project.value?.work_orders || [];
        return orders.filter((wo) => wo.status === 'completed').length;
      });

      const totalWorkOrders = computed(() => {
        return (project.value?.work_orders || []).length;
      });

      const completedOrders = computed(() => {
        const orders = project.value?.orders || [];
        return orders.filter((o) => o.status === 'completed').length;
      });

      const totalOrders = computed(() => {
        return (project.value?.orders || []).length;
      });

      const progressPercent = computed(() => {
        const total = totalWorkOrders.value + totalOrders.value;
        if (total === 0) return 0;
        const completed = completedWorkOrders.value + completedOrders.value;
        return Math.round((completed / total) * 100);
      });

      return {
        project,
        displayTitle,
        loading,
        completedWorkOrders,
        totalWorkOrders,
        completedOrders,
        totalOrders,
        progressPercent,
        formatDate,
        formatDateShort,
        getAppointmentStart,
        isTentativelyPlanned,
      };
    },
  });
</script>

<style scoped lang="scss">
  .deal-project {
    padding: 0;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      padding: 2.5rem 2rem;
      background: white;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
    }

    &__empty-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--slate-200);
      color: var(--slate-600);
    }

    &__empty-title {
      margin: 0;
      color: var(--slate-900);
      font-size: 13px;
      font-weight: 500;
    }

    &__empty-description {
      max-width: 440px;
      margin: -0.25rem 0 0;
      color: var(--slate-600);
      font-size: 13px;
      line-height: 20px;
      text-align: center;
    }

    &__empty-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      height: 36px;
      padding: 0 0.75rem;
      border: 1px solid var(--slate-300);
      border-radius: 4px;
      background: white;
      color: var(--slate-700);
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        border-color 0.15s ease;

      @include hover {
        background: var(--slate-50);
        border-color: var(--slate-400);
      }

      &:focus-visible {
        outline: 2px solid var(--brand-300);
        outline-offset: 1px;
      }
    }

    &__cancelled-banner {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      background: var(--red-50);
      border: 1px solid var(--red-200);
      border-radius: 0.375rem;
      color: var(--red-700);
      font-size: 0.8125rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &__card {
      background: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      overflow: hidden;
    }

    &__card-header {
      padding: 0.875rem 1rem;
      border-bottom: 1px solid var(--slate-100);
    }

    &__card-title-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }

    &__project-number {
      font-weight: 600;
      color: var(--slate-900);
      font-size: 0.875rem;
    }

    &__project-title {
      font-size: 0.8125rem;
      color: var(--slate-700);
      margin-top: 0.25rem;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      padding: 0.2rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.6875rem;
      font-weight: 500;
    }

    &__expected-date {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin-top: 0.375rem;
    }

    &__progress {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--slate-100);
    }

    &__progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__progress-label {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-600);
    }

    &__progress-pct {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    &__progress-bar-wrap {
      height: 5px;
      background: var(--slate-100);
      border-radius: 3px;
      overflow: hidden;
    }

    &__progress-bar-fill {
      height: 100%;
      background: var(--brand-500);
      border-radius: 3px;
      transition: width 0.2s ease;
    }

    &__progress-text {
      font-size: 0.6875rem;
      color: var(--slate-400);
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--slate-100);
    }

    &__section-title {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-600);
      margin: 0;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__list-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.375rem 0.625rem;
      background: var(--slate-50);
      border-radius: 0.25rem;
      font-size: 0.75rem;
    }

    &__item-number {
      font-weight: 500;
      color: var(--slate-800);
    }

    &__item-date {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      color: var(--slate-500);
      margin-left: auto;
      font-size: 0.6875rem;
    }

    &__tentative-icon {
      color: var(--slate-400);
      flex-shrink: 0;
    }

    &__card-footer {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 1rem;
    }
  }

  .project-status {
    &--draft {
      background-color: var(--slate-50);
      color: var(--slate-700);
    }

    &--active {
      background-color: var(--blue-50);
      color: var(--blue-700);
    }

    &--on_hold {
      background-color: var(--amber-50);
      color: var(--amber-700);
    }

    &--completed {
      background-color: var(--green-50);
      color: var(--green-700);
    }

    &--cancelled {
      background-color: var(--red-50);
      color: var(--red-700);
    }
  }

  .work-order-status {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;

    &--pending {
      background-color: var(--slate-50);
      color: var(--slate-700);
    }

    &--scheduled,
    &--assigned {
      background-color: var(--blue-50);
      color: var(--blue-700);
    }

    &--in_progress {
      background-color: var(--amber-50);
      color: var(--amber-700);
    }

    &--ready_for_installation,
    &--completed {
      background-color: var(--green-50);
      color: var(--green-700);
    }

    &--cancelled {
      background-color: var(--red-50);
      color: var(--red-700);
    }
  }
</style>
